import { useState, type ReactElement } from 'react';
import {
  budgetPercentUsed,
  modelLabel,
  tokensToEurApprox,
  useAnthropicAppConfig,
  useDeleteAnthropicAppConfig,
  type AnthropicAppConfig,
  type AnthropicAppConfigPutResponse,
} from '@/lib/anthropic';
import { AnthropicAppConfigForm } from './AnthropicAppConfigForm';
import { ConfirmDialog } from './ConfirmDialog';

/**
 * Página `/ajustes/anthropic` — dos estados según `configured`:
 *
 *  1. `!configured`             → <AnthropicAppConfigForm /> directo (estado 1).
 *  2. `configured`              → tarjeta de configuración + barra de progreso
 *                                 de presupuesto + botones editar/borrar.
 *
 * Es deliberadamente más simple que `StravaSettings` (que tiene 3 estados por
 * el doble paso "configurar app" + "OAuth"): aquí sólo hay API key.
 */
export function AnthropicSettings(): ReactElement {
  const appConfigQuery = useAnthropicAppConfig();
  const deleteMutation = useDeleteAnthropicAppConfig();

  const [savedBanner, setSavedBanner] = useState<
    { kind: 'success' | 'warning'; message: string } | null
  >(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  function handleDelete() {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
        setEditing(false);
        setSavedBanner({
          kind: 'success',
          message: 'Configuración de Anthropic borrada.',
        });
      },
    });
  }

  const appConfig: AnthropicAppConfig | undefined = appConfigQuery.data;
  const configured: boolean = appConfig?.configured ?? false;

  return (
    <section aria-labelledby="anthropic-title" className="space-y-6">
      <header>
        <h1
          id="anthropic-title"
          className="text-2xl font-semibold tracking-tight text-slate-900"
        >
          Anthropic Claude
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Configura tu API key de Claude para que el coach pueda generar
          planes. Cada profile usa su propia key y su propio presupuesto.
        </p>
      </header>

      {savedBanner && (
        <div
          role="status"
          className={
            savedBanner.kind === 'success'
              ? 'rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900'
              : 'rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900'
          }
        >
          {savedBanner.message}
        </div>
      )}

      {appConfigQuery.isLoading && (
        <p role="status" aria-live="polite" className="text-slate-700">
          Cargando configuración de Anthropic…
        </p>
      )}

      {appConfigQuery.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo cargar la configuración de Anthropic.{' '}
          {appConfigQuery.error?.message ?? ''}
        </p>
      )}

      {/* Estado 1: sin configurar */}
      {appConfig && !configured && (
        <AnthropicAppConfigForm
          mode="create"
          onSaved={() => {
            setSavedBanner({
              kind: 'success',
              message:
                'API key guardada. Ya puedes pedir planes al coach desde el calendario.',
            });
          }}
        />
      )}

      {/* Estado 2: configurado */}
      {appConfig && configured && (
        <AppConfigCard
          appConfig={appConfig}
          editing={editing}
          onEdit={() => setEditing(true)}
          onCancelEdit={() => setEditing(false)}
          onAskDelete={() => setConfirmDeleteOpen(true)}
          onSaved={(response) => {
            setEditing(false);
            if (response.apiKeyChanged) {
              setSavedBanner({
                kind: 'warning',
                message:
                  'Configuración actualizada con una nueva API key. El contador de tokens del mes se ha reseteado.',
              });
            } else {
              setSavedBanner({
                kind: 'success',
                message: 'Configuración de Anthropic actualizada.',
              });
            }
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="¿Borrar configuración de Anthropic?"
        description={
          <>
            Se borrará tu API key cifrada y el contador de tokens consumidos
            este mes. El coach dejará de poder generar planes hasta que
            vuelvas a configurar una key.
          </>
        }
        confirmLabel={deleteMutation.isPending ? 'Borrando…' : 'Borrar config'}
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card: configuración actual + uso del presupuesto
// ---------------------------------------------------------------------------

function AppConfigCard({
  appConfig,
  editing,
  onEdit,
  onCancelEdit,
  onAskDelete,
  onSaved,
}: {
  appConfig: AnthropicAppConfig;
  editing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onAskDelete: () => void;
  onSaved: (response: AnthropicAppConfigPutResponse) => void;
}): ReactElement {
  if (editing) {
    const formProps: Parameters<typeof AnthropicAppConfigForm>[0] = {
      mode: 'edit',
      onSaved,
      onCancel: onCancelEdit,
    };
    if (appConfig.authMode) formProps.initialAuthMode = appConfig.authMode;
    if (appConfig.modelDefault)
      formProps.initialModelDefault = appConfig.modelDefault;
    if (appConfig.monthlyBudgetEur !== undefined)
      formProps.initialMonthlyBudgetEur = appConfig.monthlyBudgetEur;
    return <AnthropicAppConfigForm {...formProps} />;
  }

  const authMode = appConfig.authMode ?? 'api_key';
  const isClaudeCode = authMode === 'claude_code';
  const tokens = appConfig.tokensConsumedCurrentMonth ?? 0;
  const budgetEur = appConfig.monthlyBudgetEur ?? 0;
  const consumedEur = tokensToEurApprox(tokens);
  const pct = budgetPercentUsed(tokens, budgetEur);

  return (
    <section
      aria-labelledby="anthropic-app-config-title"
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h2
        id="anthropic-app-config-title"
        className="text-lg font-semibold text-slate-900"
      >
        Configuración actual
      </h2>
      <dl className="mt-3 space-y-2 text-sm">
        <Row label="Modo">
          <span className="text-slate-900">
            {isClaudeCode
              ? 'Claude Max (claude-code CLI)'
              : 'API key (Anthropic Console)'}
          </span>
        </Row>
        <Row label="Modelo">
          <span className="text-slate-900">
            {appConfig.modelDefault ? modelLabel(appConfig.modelDefault) : '—'}
          </span>
        </Row>
        {!isClaudeCode && (
          <Row label="Presupuesto">
            <span className="text-slate-900">
              {budgetEur} €/mes
              {appConfig.currentMonthYYYYMM && (
                <span className="ml-2 text-xs text-slate-500">
                  (mes en curso: {formatYYYYMM(appConfig.currentMonthYYYYMM)})
                </span>
              )}
            </span>
          </Row>
        )}
        {appConfig.updatedAt && (
          <Row label="Actualizado">
            <span className="text-slate-900">{formatDate(appConfig.updatedAt)}</span>
          </Row>
        )}
      </dl>

      {isClaudeCode ? (
        <p
          role="note"
          className="mt-5 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700"
        >
          Modo Claude Max — sin contador de tokens. Asegúrate de que el CLI
          <code className="mx-1 rounded bg-slate-200 px-1">claude</code>
          está autenticado en el servidor.
        </p>
      ) : (
        <BudgetProgress
          tokensConsumed={tokens}
          consumedEur={consumedEur}
          budgetEur={budgetEur}
          pct={pct}
        />
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          Editar
        </button>
        <button
          type="button"
          onClick={onAskDelete}
          className="rounded-md border border-rose-300 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-600 focus:ring-offset-2"
        >
          Borrar config
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Barra de progreso del presupuesto
// ---------------------------------------------------------------------------

function BudgetProgress({
  tokensConsumed,
  consumedEur,
  budgetEur,
  pct,
}: {
  tokensConsumed: number;
  consumedEur: number;
  budgetEur: number;
  pct: number;
}): ReactElement {
  // Colores según severidad. Cumple el contrato: <70% verde, 70-90% ámbar,
  // >90% rojo. Para que el contraste sea AA usamos tonos 600.
  let barColor = 'bg-emerald-600';
  let trackBgWarn: 'none' | 'amber' | 'rose' = 'none';
  if (pct >= 90) {
    barColor = 'bg-rose-600';
    trackBgWarn = 'rose';
  } else if (pct >= 70) {
    barColor = 'bg-amber-500';
    trackBgWarn = 'amber';
  }
  const widthStyle = { width: `${Math.max(0, Math.min(100, pct))}%` };
  const pctLabel = pct >= 1 ? `${Math.round(pct)}%` : pct > 0 ? '<1%' : '0%';

  // Si el budget es 0 mostramos un mensaje específico — la barra se queda al
  // 100% rojo automáticamente vía `budgetPercentUsed`.
  const noBudget = budgetEur <= 0;

  return (
    <div className="mt-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-slate-800">Consumo este mes</p>
        <p className="text-xs text-slate-600">
          {formatTokens(tokensConsumed)} tokens
          <span className="ml-1 text-slate-500">
            (≈ {formatEur(consumedEur)})
          </span>
          {!noBudget && (
            <span className="ml-2 text-slate-500">
              de {budgetEur} € · <strong className="text-slate-700">{pctLabel}</strong>
            </span>
          )}
        </p>
      </div>
      <div
        role="progressbar"
        aria-label="Presupuesto mensual consumido"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        aria-valuetext={
          noBudget
            ? 'Sin presupuesto asignado'
            : `${Math.round(pct)} por ciento de ${budgetEur} euros consumido`
        }
        className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-200"
      >
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={widthStyle}
        />
      </div>

      {noBudget && (
        <p
          role="note"
          className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          No tienes presupuesto asignado. El coach no podrá generar planes
          mientras el presupuesto sea 0 €.
        </p>
      )}
      {!noBudget && trackBgWarn === 'amber' && (
        <p className="mt-2 text-xs text-amber-800">
          Vas por encima del 70% del presupuesto del mes. Considera subirlo
          si esperas pedir más planes.
        </p>
      )}
      {!noBudget && trackBgWarn === 'rose' && (
        <p
          role="alert"
          className="mt-2 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800"
        >
          Has alcanzado o superado el 90% del presupuesto. Cuando llegue al
          100% el coach dejará de funcionar hasta el mes siguiente o hasta
          que subas el límite.
        </p>
      )}
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: ReactElement | string;
}): ReactElement {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-2 text-sm">
      <dt className="text-slate-600">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  return new Date(iso).toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatYYYYMM(yyyymm: string): string {
  if (!/^\d{6}$/.test(yyyymm)) return yyyymm;
  const year = yyyymm.slice(0, 4);
  const month = yyyymm.slice(4, 6);
  return `${month}/${year}`;
}

function formatTokens(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  return n.toLocaleString('es-ES');
}

function formatEur(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0,00 €';
  return n.toLocaleString('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
